import React, { useState } from "react";
import {
  ChevronDownIcon,
  ChevronUpIcon,
  SearchIcon,
  FolderIcon,
  CrosshairIcon,
  WrenchIcon,
} from "lucide-react";
import { NavLink, useParams } from "react-router-dom";
import { FileIcon } from "../FileIcon";
import { FilePill } from "../FilePill";
import { links } from "@/src/utils/links";
import {
  FileType,
  useDataRoomFileQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { classNames } from "@/src/utils/cn";
import { AnimatePresence, motion } from "framer-motion";
import { animatedEmphasisClassName } from "./loading";
import { SearchFileResultCard } from "./SearchFileResultCard";

/**
 * Common block shape shared by DealThreadMessageContentBlock and SuggestedAnswerBlock.
 * Both GraphQL types satisfy this interface for their tool-related fields.
 */
export interface AiBlockFields {
  type: string;
  durationMs?: number | null;
  text?: string | null;
  reasoning?: string | null;
  toolName?: string | null;
  toolArgs?: string | null;
  toolCallId?: string | null;
  toolResult?: string | null;
  dataRoomFileName?: string | null;
  dataRoomFileType?: FileType | null;
}

// ── JSON helpers ─────────────────────────────────────────────────────────────

export function parseMaybeJson(raw?: string | null): unknown | null {
  if (!raw || raw.trim().length === 0) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

// ── Tool arg / result parsers ────────────────────────────────────────────────

export function parseSearchFilesQuery(toolArgs?: string | null): string {
  const parsedArgs = parseMaybeJson(toolArgs);
  if (
    parsedArgs &&
    typeof parsedArgs === "object" &&
    "query" in parsedArgs &&
    typeof parsedArgs.query === "string"
  ) {
    return parsedArgs.query;
  }

  return "";
}

export function parseSearchFileToolArgs(toolArgs?: string | null): {
  query: string;
  fileId: string;
} {
  const parsedArgs = parseMaybeJson(toolArgs);
  if (!parsedArgs || typeof parsedArgs !== "object") {
    return { query: "", fileId: "" };
  }

  const query =
    "query" in parsedArgs && typeof parsedArgs.query === "string"
      ? parsedArgs.query
      : "";
  const fileId =
    "file_id" in parsedArgs && typeof parsedArgs.file_id === "string"
      ? parsedArgs.file_id
      : "";

  return { query, fileId };
}

export type SearchFileToolResultItem = {
  pageIndex?: number;
  previewText?: string;
  rectsOnPage?: number[][];
  fileId?: string;
  fileName?: string;
  fileType?: FileType;
};

export type SearchFilesToolResultItem = {
  id: string;
  name?: string;
};

export function parseSearchFileToolResult(
  toolResult?: string | null,
): SearchFileToolResultItem[] {
  const parsed = parseMaybeJson(toolResult);

  if (
    !parsed ||
    typeof parsed !== "object" ||
    !("data" in parsed) ||
    !Array.isArray(parsed.data)
  ) {
    return [];
  }

  return parsed.data
    .filter((item): item is Record<string, unknown> => {
      return !!item && typeof item === "object";
    })
    .map((item) => {
      const fileId =
        (typeof item.fileId === "string" && item.fileId) ||
        (typeof item.file_id === "string" && item.file_id) ||
        (typeof item.dataRoomFileId === "string" && item.dataRoomFileId) ||
        undefined;
      return {
        pageIndex: typeof item.pageIndex === "number" ? item.pageIndex : undefined,
        previewText:
          typeof item.previewText === "string" ? item.previewText : undefined,
        rectsOnPage: Array.isArray(item.rectsOnPage)
          ? (item.rectsOnPage as number[][])
          : undefined,
        fileId,
      };
    });
}

export function parseSearchFilesToolResult(
  toolResult?: string | null,
): SearchFilesToolResultItem[] {
  const parsed = parseMaybeJson(toolResult);

  const fromParsedPayload = (() => {
    if (!parsed || typeof parsed !== "object") {
      return [] as SearchFilesToolResultItem[];
    }

    const candidateResults = (() => {
      if ("results" in parsed && Array.isArray(parsed.results)) {
        return parsed.results;
      }
      if ("data" in parsed && Array.isArray(parsed.data)) {
        return parsed.data;
      }
      return [];
    })();

    return candidateResults
      .filter((item): item is Record<string, unknown> => {
        return !!item && typeof item === "object";
      })
      .map((item) => {
        const nestedFile =
          "file" in item && item.file && typeof item.file === "object"
            ? (item.file as Record<string, unknown>)
            : null;

        const id =
          (typeof item.id === "string" && item.id) ||
          (typeof item.file_id === "string" && item.file_id) ||
          (typeof item.dataRoomFileId === "string" && item.dataRoomFileId) ||
          (typeof item.dataRoomFileID === "string" && item.dataRoomFileID) ||
          (nestedFile && typeof nestedFile.id === "string" ? nestedFile.id : "") ||
          "";

        const name =
          (typeof item.name === "string" && item.name) ||
          (nestedFile && typeof nestedFile.name === "string"
            ? nestedFile.name
            : undefined);

        return { id, name };
      })
      .filter((item) => item.id !== "");
  })();

  if (fromParsedPayload.length > 0) {
    return fromParsedPayload;
  }

  const raw = toolResult || "";
  const ids = raw.match(/dtrmfile_[A-Za-z0-9]+/g) || [];
  return ids.map((id) => ({ id }));
}

export function parseAddReferencesToolResult(
  toolResult?: string | null,
): SearchFileToolResultItem[] {
  const parsed = parseMaybeJson(toolResult);

  if (
    !parsed ||
    typeof parsed !== "object" ||
    !("references" in parsed) ||
    !Array.isArray(parsed.references)
  ) {
    return [];
  }

  return parsed.references
    .filter((item): item is Record<string, unknown> => {
      return !!item && typeof item === "object";
    })
    .map((item) => {
      const fileId =
        (typeof item.dataRoomFileId === "string" && item.dataRoomFileId) ||
        (typeof item.fileId === "string" && item.fileId) ||
        undefined;
      const fileName =
        typeof item.dataRoomFileName === "string"
          ? item.dataRoomFileName
          : undefined;
      const rawFileType =
        typeof item.dataRoomFileType === "string" ? item.dataRoomFileType : "";
      const fileType = Object.values(FileType).includes(rawFileType as FileType)
        ? (rawFileType as FileType)
        : FileType.Other;

      return {
        pageIndex: typeof item.pageIndex === "number" ? item.pageIndex : undefined,
        previewText:
          typeof item.previewText === "string" ? item.previewText : undefined,
        rectsOnPage: Array.isArray(item.rectsOnPage)
          ? (item.rectsOnPage as number[][])
          : undefined,
        fileId,
        fileName,
        fileType,
      };
    });
}

export function parseAddReferencesToolArgs(toolArgs?: string | null): { fileId: string } {
  const parsed = parseMaybeJson(toolArgs);
  if (!parsed || typeof parsed !== "object") {
    return { fileId: "" };
  }
  const fileId =
    "file_id" in parsed && typeof parsed.file_id === "string"
      ? parsed.file_id
      : "fileId" in parsed && typeof parsed.fileId === "string"
        ? parsed.fileId
        : "";
  return { fileId };
}

export function parseResolveReferenceToolResult(
  toolResult?: string | null,
): { fileId: string; refs: SearchFileToolResultItem[] } {
  let parsed = parseMaybeJson(toolResult);
  if (typeof parsed === "string") {
    parsed = parseMaybeJson(parsed);
  }

  if (!parsed || typeof parsed !== "object") {
    return { fileId: "", refs: [] };
  }

  const fileId =
    "file_id" in parsed && typeof parsed.file_id === "string"
      ? parsed.file_id
      : "";

  const rawRefs =
    "refs" in parsed && Array.isArray(parsed.refs) ? parsed.refs : [];

  const refs = rawRefs
    .filter((item): item is Record<string, unknown> => {
      return !!item && typeof item === "object";
    })
    .map((item) => ({
      pageIndex: typeof item.pageIndex === "number" ? item.pageIndex : undefined,
      previewText:
        typeof item.previewText === "string" ? item.previewText : undefined,
      rectsOnPage: Array.isArray(item.rectsOnPage)
        ? (item.rectsOnPage as number[][])
        : undefined,
      fileId,
    }));

  return { fileId, refs };
}

// ── Tool call display components ─────────────────────────────────────────────

export function SearchFileResultsHorizontalList(props: {
  results: SearchFileToolResultItem[];
  defaultFileId: string;
  defaultFileName: string;
  defaultFileType: FileType;
}) {
  const { results, defaultFileId, defaultFileName, defaultFileType } = props;

  if (results.length === 0) {
    return null;
  }

  return (
    <div className="overflow-x-auto no-scrollbar pb-1">
      <div className="flex items-center gap-x-2 w-max min-w-full">
        {results.map((result, index) => {
          const fileId = result.fileId ?? defaultFileId;
          const useDefaultFileInfo = !result.fileId && !result.fileName;
          return (
            <div key={`${fileId}-${result.pageIndex ?? "no-page"}-${index}`} className="flex-shrink-0 w-4/5 max-w-80">
              <SearchFileResultCard
                fileId={fileId}
                fileName={
                  result.fileName ??
                  (useDefaultFileInfo ? defaultFileName : undefined)
                }
                fileType={
                  result.fileType ??
                  (useDefaultFileInfo ? defaultFileType : undefined)
                }
                pageIndex={result.pageIndex}
                previewText={result.previewText}
                rectsOnPage={result.rectsOnPage}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SearchFilesResultPill(props: { fileId: string; fallbackName?: string }) {
  const client = useGqlClient();
  const fileQuery = useDataRoomFileQuery(
    client,
    { id: props.fileId },
    {
      enabled: props.fileId !== "",
      queryKey: ["AssistantSearchFilesTool", { id: props.fileId }],
    },
  );

  const resolvedName =
    fileQuery.data?.dataRoomFile.name || props.fallbackName || props.fileId;
  const resolvedType = fileQuery.data?.dataRoomFile.fileType || FileType.Other;

  return (
    <FilePill
      id={props.fileId}
      name={resolvedName}
      type={resolvedType}
      showDetailsCard={false}
    />
  );
}

// ── Individual tool call block components ────────────────────────────────────

export function AiMessageSearchFileToolCallBlock(props: {
  block: AiBlockFields;
}) {
  const client = useGqlClient();
  const { dealId } = useParams<{ dealId: string }>();
  const { query, fileId } = parseSearchFileToolArgs(props.block.toolArgs);
  const results = parseSearchFileToolResult(props.block.toolResult);
  const [showResults, setShowResults] = useState(false);
  const fileQuery = useDataRoomFileQuery(
    client,
    { id: fileId },
    {
      enabled: fileId !== "",
      queryKey: ["AssistantSearchFileTool", { id: fileId }],
    },
  );
  const fileName =
    props.block.dataRoomFileName || fileQuery.data?.dataRoomFile.name || fileId || "File";
  const fileType =
    props.block.dataRoomFileType ||
    fileQuery.data?.dataRoomFile.fileType ||
    FileType.Other;
  const fileFolderId = fileQuery.data?.dataRoomFile.folderId;
  const hasFileLink = !!dealId && !!fileFolderId && !!fileId;
  const hasResults = results.length > 0;

  return (
    <div className="space-y-1.5 w-full">
      <button
        type="button"
        onClick={() => {
          if (hasResults) {
            setShowResults((prev) => !prev);
          }
        }}
        className={classNames(
          "flex w-full items-center justify-between rounded-md px-1 py-0.5",
          hasResults ? "hover:bg-gray-100 cursor-pointer" : "cursor-default",
        )}
      >
        <div className="flex items-center gap-x-2 min-w-0 flex-wrap">
          <p className="text-sm text-gray-500">
            <span className="font-semibold text-concrete-700">Searched</span>{" "}
            <span>{query || "..."}</span>
          </p>
          {fileId ? (
            hasFileLink ? (
              <NavLink
                to={links.fileLink(fileId, fileFolderId, dealId)}
                className="inline-flex items-center gap-x-1 rounded-md border border-concrete-200 bg-white px-1.5 py-0.5 min-w-0 max-w-full overflow-hidden hover:border-concrete-400 hover:shadow-sm transition-all"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-xs text-gray-500 flex-shrink-0">in</span>
                <FileIcon fileType={fileType} size="s" />
                <span className="text-xs font-medium text-concrete-700 truncate min-w-0 max-w-full">
                  {fileName}
                </span>
              </NavLink>
            ) : (
              <div className="inline-flex items-center gap-x-1 rounded-md border border-concrete-200 bg-white px-1.5 py-0.5 min-w-0 max-w-full overflow-hidden">
                <span className="text-xs text-gray-500 flex-shrink-0">in</span>
                <FileIcon fileType={fileType} size="s" />
                <span className="text-xs font-medium text-concrete-700 truncate min-w-0 max-w-full">
                  {fileName}
                </span>
              </div>
            )
          ) : null}
        </div>
        {hasResults ? (
          <div className="flex items-center gap-x-1.5 flex-shrink-0">
            <span className="text-xs text-gray-500">{results.length} result{results.length !== 1 ? "s" : ""}</span>
            {showResults ? (
              <ChevronUpIcon className="h-4 w-4 text-concrete-500" />
            ) : (
              <ChevronDownIcon className="h-4 w-4 text-concrete-500" />
            )}
          </div>
        ) : null}
      </button>
      {hasResults && showResults ? (
        <SearchFileResultsHorizontalList
          results={results}
          defaultFileId={fileId}
          defaultFileName={fileName}
          defaultFileType={fileType}
        />
      ) : null}
    </div>
  );
}

export function AiMessageSearchFilesToolCallBlock(props: {
  block: AiBlockFields;
}) {
  const query = parseSearchFilesQuery(props.block.toolArgs);
  const hasToolResult = !!props.block.toolResult?.trim();
  const toolResults = parseSearchFilesToolResult(props.block.toolResult);
  const [showToolResult, setShowToolResult] = useState(false);
  const uniqueToolResults = toolResults.filter(
    (result, index, all) => all.findIndex((other) => other.id === result.id) === index,
  );

  return (
    <div className="space-y-1.5 w-full">
      <button
        type="button"
        onClick={() => {
          if (hasToolResult) {
            setShowToolResult((prev) => !prev);
          }
        }}
        className={classNames(
          "flex w-full items-start justify-between gap-x-2 rounded-md px-1 py-0.5",
          hasToolResult ? "hover:bg-gray-100 cursor-pointer" : "cursor-default",
        )}
      >
        <p className="flex items-start gap-x-2 min-w-0 text-sm text-left">
          <SearchIcon className="w-4 h-4 text-persian-900 flex-shrink-0 mt-0.5" />
          <span>
            <span
              className={
                hasToolResult ? "font-semibold text-concrete-700" : animatedEmphasisClassName
              }
            >
              {hasToolResult ? "Searched files for" : "Searching files for"}
            </span>
            <span className="text-gray-500">{" "}{query || "..."}</span>
          </span>
        </p>
        {hasToolResult ? (
          showToolResult ? (
            <ChevronUpIcon className="h-4 w-4 text-concrete-500 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-concrete-500 flex-shrink-0 mt-0.5" />
          )
        ) : null}
      </button>
      {hasToolResult && showToolResult && uniqueToolResults.length > 0 ? (
        <div className="overflow-x-auto no-scrollbar pb-1">
          <div className="flex items-center gap-x-2 w-max min-w-full">
            {uniqueToolResults.map((result) => (
              <SearchFilesResultPill
                key={result.id}
                fileId={result.id}
                fallbackName={result.name}
              />
            ))}
          </div>
        </div>
      ) : null}
      {hasToolResult && showToolResult && uniqueToolResults.length === 0 ? (
        <p className="text-xs text-gray-500 px-1">No results</p>
      ) : null}
    </div>
  );
}

export function AiMessageAddReferencesToolCallBlock(props: {
  block: AiBlockFields;
}) {
  const client = useGqlClient();
  const results = parseAddReferencesToolResult(props.block.toolResult);
  const { fileId: argsFileId } = parseAddReferencesToolArgs(props.block.toolArgs);
  const defaultFileId = argsFileId || results[0]?.fileId || "";
  const fileQuery = useDataRoomFileQuery(
    client,
    { id: defaultFileId },
    {
      enabled: defaultFileId !== "" && results.some((r) => !r.fileName),
      queryKey: ["AssistantAddReferencesTool", { id: defaultFileId }],
    },
  );
  const defaultFileName =
    fileQuery.data?.dataRoomFile.name || defaultFileId || "File";
  const defaultFileType =
    fileQuery.data?.dataRoomFile.fileType || FileType.Other;

  if (results.length === 0) {
    return null;
  }

  return (
    <SearchFileResultsHorizontalList
      results={results}
      defaultFileId={defaultFileId}
      defaultFileName={defaultFileName}
      defaultFileType={defaultFileType}
    />
  );
}

export function AiMessageListFoldersToolCallBlock(props: {
  block: AiBlockFields;
}) {
  const hasToolResult = !!props.block.toolResult?.trim();

  return (
    <div className="flex items-center gap-x-2 px-1 py-0.5">
      <FolderIcon className="w-4 h-4 text-persian-900 flex-shrink-0" />
      <p className="text-sm">
        <span
          className={
            hasToolResult ? "font-semibold text-concrete-700" : animatedEmphasisClassName
          }
        >
          {hasToolResult ? "Browsed folders" : "Browsing folders..."}
        </span>
      </p>
    </div>
  );
}

export function AiMessageResolveReferenceToolCallBlock(props: {
  block: AiBlockFields;
}) {
  const client = useGqlClient();
  const { fileId, refs } = parseResolveReferenceToolResult(props.block.toolResult);
  const hasResult = refs.length > 0;
  const [showRefs, setShowRefs] = useState(false);

  const fileQuery = useDataRoomFileQuery(
    client,
    { id: fileId },
    {
      enabled: fileId !== "",
      queryKey: ["AssistantResolveReferenceTool", { id: fileId }],
    },
  );

  const fileName = fileQuery.data?.dataRoomFile.name || fileId || "File";
  const fileType = fileQuery.data?.dataRoomFile.fileType || FileType.Other;

  return (
    <div className="space-y-1.5 w-full">
      <button
        type="button"
        onClick={() => {
          if (hasResult) {
            setShowRefs((prev) => !prev);
          }
        }}
        className={classNames(
          "flex w-full items-start justify-between gap-x-2 rounded-md px-1 py-0.5",
          hasResult ? "hover:bg-gray-100 cursor-pointer" : "cursor-default",
        )}
      >
        <p className="flex items-start gap-x-2 min-w-0 text-sm text-left">
          <CrosshairIcon className="w-4 h-4 text-persian-900 flex-shrink-0 mt-0.5" />
          <span>
            <span
              className={
                hasResult ? "font-semibold text-concrete-700" : animatedEmphasisClassName
              }
            >
              {hasResult ? "Located reference" : "Locating reference..."}
            </span>
            {hasResult ? (
              <span className="text-gray-500">
                {" "}{refs.length} match{refs.length !== 1 ? "es" : ""} in {fileName}
              </span>
            ) : null}
          </span>
        </p>
        {hasResult ? (
          showRefs ? (
            <ChevronUpIcon className="h-4 w-4 text-concrete-500 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronDownIcon className="h-4 w-4 text-concrete-500 flex-shrink-0 mt-0.5" />
          )
        ) : null}
      </button>
      {hasResult && showRefs ? (
        <SearchFileResultsHorizontalList
          results={refs}
          defaultFileId={fileId}
          defaultFileName={fileName}
          defaultFileType={fileType}
        />
      ) : null}
    </div>
  );
}

// ── Dispatcher ───────────────────────────────────────────────────────────────

export function AiMessageToolCallBlock(props: { block: AiBlockFields }) {
  if (props.block.toolName === "search_file") {
    return <AiMessageSearchFileToolCallBlock block={props.block} />;
  }

  if (props.block.toolName === "search_files") {
    return <AiMessageSearchFilesToolCallBlock block={props.block} />;
  }

  if (props.block.toolName === "add_references") {
    return <AiMessageAddReferencesToolCallBlock block={props.block} />;
  }

  if (props.block.toolName === "list_folders") {
    return <AiMessageListFoldersToolCallBlock block={props.block} />;
  }

  if (props.block.toolName === "resolve_reference") {
    return <AiMessageResolveReferenceToolCallBlock block={props.block} />;
  }

  const parsedArgs = parseMaybeJson(props.block.toolArgs);
  const parsedResult = parseMaybeJson(props.block.toolResult);

  return (
    <div className="w-full min-w-0 rounded-md border border-concrete-200 bg-white px-3 py-2 text-xs text-gray-700 space-y-2">
      <p className="font-semibold text-concrete-700">
        Tool Call: {props.block.toolName || "unknown"}
      </p>
      <div>
        <p className="font-medium text-concrete-600">Query</p>
        <pre className="mt-0.5 font-mono whitespace-pre-wrap break-all max-w-full overflow-x-auto text-gray-600">
          {parsedArgs
            ? JSON.stringify(parsedArgs, null, 2)
            : props.block.toolArgs || "(none)"}
        </pre>
      </div>
      <div>
        <p className="font-medium text-concrete-600">Result</p>
        <pre className="mt-0.5 font-mono whitespace-pre-wrap break-all max-w-full overflow-x-auto text-gray-600">
          {parsedResult
            ? JSON.stringify(parsedResult, null, 2)
            : props.block.toolResult || "(none)"}
        </pre>
      </div>
    </div>
  );
}

// ── Tool call summary ────────────────────────────────────────────────────────

export type ToolSummaryToken = {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
};

export const TOOL_DISPLAY: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    labelSingular: string;
    labelPlural: string;
  }
> = {
  search_file: {
    icon: SearchIcon,
    labelSingular: "Searched file",
    labelPlural: "Searched {n} files",
  },
  search_files: {
    icon: SearchIcon,
    labelSingular: "Searched files",
    labelPlural: "Searched files ({n})",
  },
  list_folders: {
    icon: FolderIcon,
    labelSingular: "Browsed folders",
    labelPlural: "Browsed folders ({n})",
  },
  resolve_reference: {
    icon: CrosshairIcon,
    labelSingular: "Located reference",
    labelPlural: "Located {n} references",
  },
};

export const HIDDEN_TOOL_SUMMARY = new Set(["add_references", "add_files"]);

export function buildToolSummaryTokens(
  blocks: AiBlockFields[],
): ToolSummaryToken[] {
  const counts = new Map<string, number>();
  for (const block of blocks) {
    const name = block.toolName || "unknown";
    if (HIDDEN_TOOL_SUMMARY.has(name)) continue;
    counts.set(name, (counts.get(name) || 0) + 1);
  }

  const tokens: ToolSummaryToken[] = [];
  for (const [name, count] of counts) {
    const display = TOOL_DISPLAY[name];
    if (display) {
      const label =
        count === 1
          ? display.labelSingular
          : display.labelPlural.replace("{n}", String(count));
      tokens.push({ icon: display.icon, label });
    } else {
      tokens.push({
        icon: WrenchIcon,
        label: count === 1 ? name : `${name} (${count})`,
      });
    }
  }

  return tokens;
}

export function ToolCallSummaryLine(props: {
  blocks: AiBlockFields[];
  expanded: boolean;
  onToggle: () => void;
}) {
  const tokens = buildToolSummaryTokens(props.blocks);

  if (tokens.length === 0) return null;

  return (
    <button
      type="button"
      onClick={props.onToggle}
      className="flex w-full items-center justify-between gap-x-2 rounded-md border border-concrete-200 bg-concrete-50 px-2.5 py-1.5 hover:bg-concrete-100 transition-colors cursor-pointer"
    >
      <div className="flex items-center gap-x-1 min-w-0 flex-wrap text-sm">
        {tokens.map((token, i) => {
          const IconComp = token.icon;
          return (
            <React.Fragment key={`${token.label}-${i}`}>
              {i > 0 ? (
                <span className="text-concrete-300 mx-0.5">&middot;</span>
              ) : null}
              <span className="inline-flex items-center gap-x-1 text-concrete-600">
                <IconComp className="w-3.5 h-3.5 text-persian-900 flex-shrink-0" />
                <span className="font-medium text-concrete-700">{token.label}</span>
              </span>
            </React.Fragment>
          );
        })}
      </div>
      {props.expanded ? (
        <ChevronUpIcon className="h-4 w-4 text-concrete-500 flex-shrink-0" />
      ) : (
        <ChevronDownIcon className="h-4 w-4 text-concrete-500 flex-shrink-0" />
      )}
    </button>
  );
}

// ── Block grouping (used by deal assistant) ──────────────────────────────────

export type GroupedBlock =
  | { kind: "single"; block: AiBlockFields; index: number }
  | { kind: "toolGroup"; blocks: AiBlockFields[]; startIndex: number; followedByContent: boolean };

export function groupBlocks(
  blocks: AiBlockFields[],
  toolCallType: string,
): GroupedBlock[] {
  const groups: GroupedBlock[] = [];
  let currentToolGroup: AiBlockFields[] = [];
  let toolGroupStartIndex = 0;

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (block.type === toolCallType) {
      if (currentToolGroup.length === 0) {
        toolGroupStartIndex = i;
      }
      currentToolGroup.push(block);
    } else {
      if (currentToolGroup.length > 0) {
        groups.push({
          kind: "toolGroup",
          blocks: currentToolGroup,
          startIndex: toolGroupStartIndex,
          followedByContent: true,
        });
        currentToolGroup = [];
      }
      groups.push({ kind: "single", block, index: i });
    }
  }

  if (currentToolGroup.length > 0) {
    groups.push({
      kind: "toolGroup",
      blocks: currentToolGroup,
      startIndex: toolGroupStartIndex,
      followedByContent: false,
    });
  }

  return groups;
}

// ── ToolCallLiveTicker (used by deal assistant) ──────────────────────────────

export function ToolCallLiveTicker(props: {
  blocks: AiBlockFields[];
}) {
  const visibleCount = 3;
  const blocks = props.blocks;
  const startIdx = Math.max(0, blocks.length - visibleCount);
  const visibleBlocks = blocks.slice(startIdx);
  const hiddenCount = startIdx;

  return (
    <div className="w-full space-y-1 px-1 py-0.5">
      {hiddenCount > 0 ? (
        <p className="text-xs text-concrete-400 px-1">
          +{hiddenCount} earlier step{hiddenCount !== 1 ? "s" : ""}
        </p>
      ) : null}
      <AnimatePresence initial={false}>
        {visibleBlocks.map((block, i) => {
          const key = block.toolCallId ?? `ticker-${startIdx + i}`;
          const isActive = !block.toolResult;

          return (
            <motion.div
              key={key}
              initial={{ opacity: 0, y: 10, height: 0 }}
              animate={{ opacity: isActive ? 1 : 0.5, y: 0, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
              className="overflow-hidden"
            >
              <AiMessageToolCallBlock block={block} />
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

// ── ToolCallGroup (used by deal assistant) ───────────────────────────────────

export function ToolCallGroup(props: {
  blocks: AiBlockFields[];
  followedByContent: boolean;
}) {
  const hasPendingCalls = props.blocks.some((b) => !b.toolResult);
  const isLive = hasPendingCalls || !props.followedByContent;
  const [expanded, setExpanded] = useState(false);

  if (isLive) {
    return <ToolCallLiveTicker blocks={props.blocks} />;
  }

  return (
    <div className="w-full space-y-1.5">
      <ToolCallSummaryLine
        blocks={props.blocks}
        expanded={expanded}
        onToggle={() => setExpanded((prev) => !prev)}
      />
      <AnimatePresence initial={false}>
        {expanded ? (
          <motion.div
            key="tool-call-group-expanded"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2, ease: [0.2, 0.8, 0.2, 1] }}
            className="overflow-hidden space-y-2"
          >
            {props.blocks.map((block, i) => {
              const key = block.toolCallId ?? `group-${i}`;
              return (
                <div key={key} className="w-full min-w-0">
                  <AiMessageToolCallBlock block={block} />
                </div>
              );
            })}
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
